import type { Workspace } from '@up2stay/up2promo-api-client';
import { useMemo } from 'react';

import { useWorkspaces } from '@/core/hooks/use-workspaces';

import { createHydratedStorage } from './hydrated.store';

/**
 * Current workspace selection (ID only). Full workspace objects are derived
 * from the memberships query. Null when none selected (e.g. redirect to /onboarding).
 */
interface WorkspaceState {
  selectedWorkspaceId: string | null;
  setWorkspace: (workspace: Workspace | null) => void;
  clearWorkspace: () => void;
}

export const useWorkspaceStore = createHydratedStorage<WorkspaceState>(
  (set) => ({
    selectedWorkspaceId: null,
    setWorkspace: (workspace) =>
      set({ selectedWorkspaceId: workspace?.id ?? null }),
    clearWorkspace: () => set({ selectedWorkspaceId: null }),
  }),
  {
    name: 'workspace-selection-store',
    partialize: (state) => ({
      selectedWorkspaceId: state.selectedWorkspaceId,
    }),
  },
);

export function useCurrentWorkspace(): Workspace | null {
  const selectedWorkspaceId = useWorkspaceStore(
    (state) => state.selectedWorkspaceId,
  );
  const { workspaces } = useWorkspaces();

  return useMemo(
    () => workspaces.find((ws) => ws.id === selectedWorkspaceId) ?? null,
    [selectedWorkspaceId, workspaces],
  );
}

export const useSetWorkspace = () =>
  useWorkspaceStore((state) => state.setWorkspace);

export const useSelectedWorkspaceId = () =>
  useWorkspaceStore((state) => state.selectedWorkspaceId);
